import React, { useState, useEffect } from 'react';
import { teamAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Users, CheckCircle, XCircle, X } from 'lucide-react';

const TeamInvitationBanner = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedInvitations, setDismissedInvitations] = useState([]);
  const { toast } = useToast();
  const { user } = useAuth();

  // Load invitations on mount - only if user is logged in
  useEffect(() => {
    if (user) {
      loadInvitations();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Calculate visible invitations
  const visibleInvitations = invitations.filter(
    inv => !dismissedInvitations.includes(inv.invitation_token)
  );

  // Calculate banner height for body padding
  // IMPORTANT: This hook must be called on every render (React hooks rule)
  React.useEffect(() => {
    if (visibleInvitations.length > 0 && !loading) {
      // Add padding to body to prevent content overlap
      document.body.style.paddingTop = '70px';
    } else {
      document.body.style.paddingTop = '0px';
    }
    
    return () => {
      document.body.style.paddingTop = '0px';
    };
  }, [visibleInvitations.length, loading]);

  const loadInvitations = async () => {
    try {
      const data = await teamAPI.getPendingInvitations();
      setInvitations(data || []);
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitationId) => {
    try {
      await teamAPI.acceptInvitation(invitationId);
      toast({
        title: "Success",
        description: "You've joined the team! Refreshing..."
      });
      // Reload page to refresh team data
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to accept invitation",
        variant: "destructive"
      });
    }
  };

  const handleReject = async (invitationId) => {
    try {
      await teamAPI.rejectInvitation(invitationId);
      toast({
        title: "Invitation Rejected",
        description: "You've declined the team invitation"
      });
      loadInvitations();
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reject invitation",
        variant: "destructive"
      });
    }
  };

  const handleDismiss = (invitationId) => {
    setDismissedInvitations([...dismissedInvitations, invitationId]);
  };

  // Early returns after ALL hooks
  if (loading || invitations.length === 0) {
    return null;
  }

  if (visibleInvitations.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[70] shadow-lg">
      {visibleInvitations.map((invitation) => (
        <div
          key={invitation.invitation_token}
          className="bg-amber-600 text-white"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 bg-white bg-opacity-20 rounded-full p-2">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm sm:text-base truncate">
                    Team Invitation from {invitation.team_name}
                    <span className="ml-2 text-xs bg-white bg-opacity-30 px-2 py-0.5 rounded-full">
                      New
                    </span>
                  </p>
                  <p className="text-xs sm:text-sm opacity-90 truncate">
                    {invitation.owner_name} invited you to join as <span className="font-medium">{invitation.designation}</span> 
                    {invitation.expires_at && (
                      <span className="ml-2">• Expires: {new Date(invitation.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  className="bg-white text-amber-600 hover:bg-gray-100 font-semibold"
                  onClick={() => handleAccept(invitation._id)}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:bg-opacity-20"
                  onClick={() => handleReject(invitation._id)}
                >
                  Decline
                </Button>
                <button
                  onClick={() => handleDismiss(invitation.invitation_token)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TeamInvitationBanner;
